import React, { useEffect, useRef, useState } from 'react';
import { Camera, Circle, Square, RotateCcw, Trash2, AlertTriangle } from 'lucide-react';

const MAX_SECONDS = 180;

/** Enregistreur caméra+micro pour le REX (facultatif, max 3 min).
 *  Remonte le Blob final via onVideo(blob | null). */
export const RexVideoRecorder = ({ onVideo }) => {
  const [phase, setPhase] = useState('idle'); // idle | live | recording | preview
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => {
    stopStream();
    clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const activate = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280 }, audio: true });
      streamRef.current = stream;
      setPhase('live');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch {
      setError("Impossible d'accéder à la caméra/au micro. Vérifiez les autorisations du navigateur.");
    }
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    let rec;
    try {
      rec = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8,opus' });
    } catch {
      rec = new MediaRecorder(stream);
    }
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPhase('preview');
      stopStream();
      onVideo(blob);
    };
    recorderRef.current = rec;
    rec.start(1000);
    setSeconds(0);
    setPhase('recording');
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stopRecording();
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop();
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setSeconds(0);
    onVideo(null);
    activate();
  };

  const remove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setSeconds(0);
    stopStream();
    onVideo(null);
    setPhase('idle');
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="bg-background border border-ink-600 rounded-xl p-4" data-testid="rex-video-recorder">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
        <Camera className="w-4 h-4 text-accent" /> Témoignage vidéo (facultatif — 3 min max)
      </p>

      {error && (
        <p className="text-xs text-danger flex items-center gap-1.5 mb-2" data-testid="rex-camera-error">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </p>
      )}

      {phase === 'idle' && (
        <button
          type="button"
          onClick={activate}
          className="w-full py-8 border-2 border-dashed border-ink-600 rounded-lg text-slate-500 hover:border-accent/60 hover:text-accent transition-colors text-sm font-medium"
          data-testid="rex-activate-camera"
        >
          <Camera className="w-6 h-6 mx-auto mb-2" /> Activer ma caméra et mon micro
        </button>
      )}

      {(phase === 'live' || phase === 'recording') && (
        <div>
          <div className="relative rounded-lg overflow-hidden bg-black">
            <video ref={videoRef} muted playsInline className="w-full max-h-64 object-contain" />
            {phase === 'recording' && (
              <span className="absolute top-2 left-2 flex items-center gap-1.5 bg-danger/90 text-white text-xs font-bold px-2 py-1 rounded-full" data-testid="rex-rec-timer">
                <Circle className="w-2.5 h-2.5 fill-current animate-pulse" /> {mmss(seconds)} / {mmss(MAX_SECONDS)}
              </span>
            )}
          </div>
          <div className="flex justify-center gap-2 mt-3">
            {phase === 'live' ? (
              <button type="button" onClick={startRecording} className="flex items-center gap-2 bg-danger text-white px-5 py-2 rounded-full text-sm font-bold hover:opacity-90" data-testid="rex-start-recording">
                <Circle className="w-3.5 h-3.5 fill-current" /> Démarrer l'enregistrement
              </button>
            ) : (
              <button type="button" onClick={stopRecording} className="flex items-center gap-2 bg-ink-700 text-white px-5 py-2 rounded-full text-sm font-bold hover:opacity-90" data-testid="rex-stop-recording">
                <Square className="w-3.5 h-3.5 fill-current" /> Arrêter
              </button>
            )}
            <button type="button" onClick={remove} className="px-4 py-2 text-xs text-slate-500 hover:text-slate-300">Annuler</button>
          </div>
        </div>
      )}

      {phase === 'preview' && previewUrl && (
        <div>
          <video src={previewUrl} controls playsInline className="w-full max-h-64 rounded-lg bg-black" data-testid="rex-video-preview" />
          <div className="flex justify-center gap-2 mt-3">
            <button type="button" onClick={retake} className="flex items-center gap-1.5 px-4 py-2 border border-ink-600 rounded-full text-xs font-bold text-slate-300 hover:bg-ink-700/40" data-testid="rex-retake">
              <RotateCcw className="w-3.5 h-3.5" /> Refaire la prise
            </button>
            <button type="button" onClick={remove} className="flex items-center gap-1.5 px-4 py-2 border border-danger/40 rounded-full text-xs font-bold text-danger hover:bg-danger/10" data-testid="rex-remove-video">
              <Trash2 className="w-3.5 h-3.5" /> Supprimer la vidéo
            </button>
          </div>
          <p className="text-center text-[10px] text-money mt-2">✓ Vidéo prête ({mmss(seconds)}) — elle sera envoyée avec votre retour.</p>
        </div>
      )}
    </div>
  );
};
